import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { DictionaryService, CountryDto } from '../../../../services/api/dictionary';
import { DEFAULT_INDUSTRY_ID, RegistrationService } from '../../../../services/api/registration';
import { LanguageService } from '../../../../services/i18n/language';
import { CountrySelectComponent } from '../../../shared/country-select/country-select';
import { LegalConsentComponent } from '../../../legal/legal-consent/legal-consent.component';
import { Subscription, combineLatest } from 'rxjs';

@Component({
  selector: 'app-cargo-owner-registration',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule, CountrySelectComponent, LegalConsentComponent],
  templateUrl: './cargo-owner-registration.html',
  styleUrl: './cargo-owner-registration.scss'
})
export class CargoOwnerRegistrationComponent implements OnInit, OnDestroy {
  registrationForm: FormGroup;
  countries: CountryDto[] = [];
  loading = false;
  error: string | null = null;
  success = false;

  // Переводы
  titleText = 'Регистрация грузовладельца';
  subtitleText = 'Заполните форму для создания аккаунта';
  personalInfoText = 'Личная информация';
  passwordText = 'Пароль';
  companyInfoText = 'Информация о компании';
  emailLabel = 'Email';
  fullNameLabel = 'Полное имя';
  phoneLabel = 'Телефон';
  passwordLabel = 'Пароль';
  confirmPasswordLabel = 'Подтвердите пароль';
  companyNameLabel = 'Название компании';
  countryLabel = 'Страна';
  selectCountryText = 'Выберите страну';
  submitText = 'Зарегистрироваться';
  submittingText = 'Регистрация...';
  hasAccountText = 'Уже есть аккаунт? Войти';
  successText = 'Регистрация успешно завершена! Вы будете перенаправлены на страницу входа...';
  requiredText = '*';
  fullNamePlaceholderText = 'Имя Фамилия';
  passwordPlaceholderText = 'Минимум 6 символов';
  confirmPasswordPlaceholderText = 'Повторите пароль';
  companyNamePlaceholderText = 'ИП/ТОО «Наименование»';

  private subscriptions = new Subscription();

  constructor(
    private fb: FormBuilder,
    private dictionaryService: DictionaryService,
    private registrationService: RegistrationService,
    private router: Router,
    private languageService: LanguageService,
    private cdr: ChangeDetectorRef
  ) {
    this.registrationForm = this.fb.group({
      email: ['', [Validators.required, Validators.email, Validators.maxLength(255)]],
      fullName: ['', [Validators.required, Validators.maxLength(255)]],
      phone: ['', [Validators.maxLength(20)]],
      password: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(255)]],
      confirmPassword: ['', [Validators.required]],
      companyName: ['', [Validators.required, Validators.maxLength(255)]],
      countryId: [null, [Validators.required]],
      acceptTerms: [false, [Validators.requiredTrue]],
      acceptPersonalData: [false, [Validators.requiredTrue]]
    }, { validators: this.passwordMatchValidator });
  }

  ngOnInit(): void {
    this.updateTranslations();
    this.loadDictionaries();

    // Подписываемся на изменения языка
    this.subscriptions.add(
      combineLatest([
        this.languageService.getTranslations(),
        this.languageService.getCurrentLanguage()
      ]).subscribe(() => {
        this.updateTranslations();
        this.loadDictionaries(); // Перезагружаем справочники с новым языком
        this.cdr.detectChanges();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private updateTranslations(): void {
    this.titleText = this.languageService.translate('registration.cargoOwner.title');
    this.subtitleText = this.languageService.translate('registration.cargoOwner.subtitle');
    this.personalInfoText = this.languageService.translate('registration.cargoOwner.personalInfo');
    this.passwordText = this.languageService.translate('registration.cargoOwner.password');
    this.companyInfoText = this.languageService.translate('registration.cargoOwner.companyInfo');
    this.emailLabel = this.languageService.translate('registration.cargoOwner.email');
    this.fullNameLabel = this.languageService.translate('registration.cargoOwner.fullName');
    this.phoneLabel = this.languageService.translate('registration.cargoOwner.phone');
    this.passwordLabel = this.languageService.translate('registration.cargoOwner.passwordField');
    this.confirmPasswordLabel = this.languageService.translate('registration.cargoOwner.confirmPassword');
    this.companyNameLabel = this.languageService.translate('registration.cargoOwner.companyName');
    this.countryLabel = this.languageService.translate('registration.cargoOwner.country');
    this.selectCountryText = this.languageService.translate('registration.cargoOwner.selectCountry');
    this.submitText = this.languageService.translate('registration.cargoOwner.submit');
    this.submittingText = this.languageService.translate('registration.cargoOwner.submitting');
    this.hasAccountText = this.languageService.translate('registration.cargoOwner.hasAccount');
    this.successText = this.languageService.translate('registration.cargoOwner.success');
    this.requiredText = this.languageService.translate('registration.cargoOwner.required');
    this.fullNamePlaceholderText = this.languageService.translate('registration.cargoOwner.fullNamePlaceholder') || this.fullNamePlaceholderText;
    this.passwordPlaceholderText = this.languageService.translate('registration.cargoOwner.passwordPlaceholder') || this.passwordPlaceholderText;
    this.confirmPasswordPlaceholderText = this.languageService.translate('registration.cargoOwner.confirmPasswordPlaceholder') || this.confirmPasswordPlaceholderText;
    this.companyNamePlaceholderText = this.languageService.translate('registration.cargoOwner.companyNamePlaceholder') || this.companyNamePlaceholderText;
  }

  passwordMatchValidator(form: FormGroup) {
    const password = form.get('password');
    const confirmPassword = form.get('confirmPassword');
    
    if (password && confirmPassword && password.value !== confirmPassword.value) {
      confirmPassword.setErrors({ passwordMismatch: true });
      return { passwordMismatch: true };
    }
    
    if (confirmPassword && confirmPassword.hasError('passwordMismatch')) {
      confirmPassword.setErrors(null);
    }
    
    return null;
  }

  loadDictionaries(): void {
    this.dictionaryService.getAllCountries().subscribe({
      next: (countries) => {
        this.countries = countries;
        // Казахстан выбран по умолчанию, пользователь может сменить
        const kazakhstan = countries.find(c => c.code === 'KZ');
        if (kazakhstan && !this.registrationForm.get('countryId')?.value) {
          this.registrationForm.patchValue({ countryId: kazakhstan.id });
        }
      },
      error: (err) => {
        console.error('Ошибка загрузки стран:', err);
        this.error = this.languageService.translate('registration.cargoOwner.errors.default');
      }
    });
  }

  onSubmit(): void {
    if (this.registrationForm.invalid) {
      this.markFormGroupTouched(this.registrationForm);
      return;
    }

    this.loading = true;
    this.error = null;

    const formValue = this.registrationForm.value;
    const request = {
      email: formValue.email,
      fullName: formValue.fullName,
      phone: formValue.phone || undefined,
      password: formValue.password,
      companyName: formValue.companyName,
      industryId: DEFAULT_INDUSTRY_ID,
      countryId: formValue.countryId
    };

    this.registrationService.registerCargoOwner(request).subscribe({
      next: () => {
        this.success = true;
        this.loading = false;
        setTimeout(() => {
          this.router.navigate(['/login']);
        }, 2000);
      },
      error: (err) => {
        this.loading = false;
        if (err.error && err.error.message) {
          this.error = err.error.message;
        } else if (err.error && typeof err.error === 'string') {
          this.error = err.error;
        } else {
          this.error = this.languageService.translate('registration.cargoOwner.errors.default');
        }
      }
    });
  }

  markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  getFieldError(fieldName: string): string {
    const field = this.registrationForm.get(fieldName);
    if (field && field.touched && field.errors) {
      if (field.errors['required']) {
        return this.languageService.translate('registration.cargoOwner.errors.required');
      }
      if (field.errors['email']) {
        return this.languageService.translate('registration.cargoOwner.errors.email');
      }
      if (field.errors['minlength']) {
        return this.languageService.translate('registration.cargoOwner.errors.minlength', {
          min: field.errors['minlength'].requiredLength
        });
      }
      if (field.errors['maxlength']) {
        return this.languageService.translate('registration.cargoOwner.errors.maxlength', {
          max: field.errors['maxlength'].requiredLength
        });
      }
      if (field.errors['passwordMismatch']) {
        return this.languageService.translate('registration.cargoOwner.errors.passwordMismatch');
      }
    }
    return '';
  }
}
